import {Network} from "../../../network/Network";
import {ProductType} from "../../../models/product/ProductType";
import {INetworkComponent} from "../../../network/types/INetworkComponent";
import {HttpStatus} from "../../../network/status/HttpStatus";
import {
    ICommunityEstablishmentIncoming
} from "../../../models/community/establishment/ICommunityEstablishmentIncoming";
import {
    ICommunityEstablishmentContactOutgoing,
    ICommunityEstablishmentDescriptionOutgoing,
    ICommunityEstablishmentAddressOutgoing,
    ICommunityEstablishmentCategoriesOutgoing,
    ICommunityEstablishmentHoursOutgoing,
    ICommunityEstablishmentAttributesOutgoing,
    ICommunityEstablishmentServicesOutgoing,
    ICommunityEstablishmentStatusOutgoing,
    ICommunityEstablishmentTitleOutgoing
} from "../../../models/community/establishment/ICommunityEstablishmentOutgoing";
import {
    ICommunityEstablishmentAttributesReferenceIncoming
} from "../../../models/community/establishment/ICommunityEstablishmentAttributesReferenceIncoming";
import {
    ICommunityEstablishmentGoogleUpdatedIncoming
} from "../../../models/community/establishment/ICommunityEstablishmentGoogleUpdatedIncoming";
import {
    ICommunityEstablishmentMediaIncoming,
    ICommunityEstablishmentMediaOutgoing,
    IEstablishmentMediaResponse
} from "../../../models/community/establishment/ICommunityEstablishmentMediaIncoming";
import {
    ICommunityEstablishmentCategoryIncoming
} from "../../../models/community/establishment/ICommunityEstablishmentProfileIncoming";
import {
    ICommunityEstablishmentReferenceIncoming
} from "../../../models/community/establishment/ICommunityEstablishmentReferenceIncoming";
import {
    IEstablishmentWriteResponse
} from "../../../models/community/establishment/IEstablishmentWriteResponse";

export class CommunityEstablishmentService {

    public static dispose(): void {
    }

    public static init(): void {
    }

    /**
     * Servi depuis notre base seule, sans appel à Google : l'affichage de la page ne dépend pas de la
     * disponibilité de l'API partenaire.
     */
    public static async get(communityId: string, component?: INetworkComponent): Promise<ICommunityEstablishmentIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/establishment/${communityId}`, component);
        return request?.data;
    }

    /**
     * Relit la fiche chez Google. C'est une écriture : chaque appel consomme le quota partagé par tout le
     * parc, et le back borne lui-même la fréquence.
     */
    public static async pull(communityId: string, component?: INetworkComponent): Promise<ICommunityEstablishmentIncoming> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/establishment/pull/${communityId}`, undefined, component);
        return request?.data;
    }

    /**
     * Les écritures rendent la réponse complète et non la fiche seule : un refus de Google porte le détail par
     * champ, et c'est cette information qui doit revenir jusqu'au formulaire. Les services de lecture, eux,
     * n'ont rien à dire d'autre que la donnée.
     */
    public static async updateDescription(communityId: string,
                                          data: ICommunityEstablishmentDescriptionOutgoing,
                                          component?: INetworkComponent): Promise<IEstablishmentWriteResponse> {

        return await Network.putJson(ProductType.COMMUNITY, `/community/establishment/description/${communityId}`, data, component);
    }

    public static async updateContact(communityId: string,
                                      data: ICommunityEstablishmentContactOutgoing,
                                      component?: INetworkComponent): Promise<IEstablishmentWriteResponse> {

        return await Network.putJson(ProductType.COMMUNITY, `/community/establishment/contact/${communityId}`, data, component);
    }

    /**
     * Les trois listes d'horaires partent ensemble : Google les remplace en entier et refuse les horaires
     * exceptionnels d'une fiche sans semaine type.
     */
    public static async updateHours(communityId: string,
                                    data: ICommunityEstablishmentHoursOutgoing,
                                    component?: INetworkComponent): Promise<IEstablishmentWriteResponse> {

        return await Network.putJson(ProductType.COMMUNITY, `/community/establishment/hours/${communityId}`, data, component);
    }

    public static async updateStatus(communityId: string,
                                     data: ICommunityEstablishmentStatusOutgoing,
                                     component?: INetworkComponent): Promise<IEstablishmentWriteResponse> {

        return await Network.putJson(ProductType.COMMUNITY, `/community/establishment/status/${communityId}`, data, component);
    }

    public static async updateTitle(communityId: string,
                                    data: ICommunityEstablishmentTitleOutgoing,
                                    component?: INetworkComponent): Promise<IEstablishmentWriteResponse> {

        return await Network.putJson(ProductType.COMMUNITY, `/community/establishment/title/${communityId}`, data, component);
    }

    public static async updateCategories(communityId: string,
                                         data: ICommunityEstablishmentCategoriesOutgoing,
                                         component?: INetworkComponent): Promise<IEstablishmentWriteResponse> {

        return await Network.putJson(ProductType.COMMUNITY, `/community/establishment/categories/${communityId}`, data, component);
    }

    public static async updateAddress(communityId: string,
                                      data: ICommunityEstablishmentAddressOutgoing,
                                      component?: INetworkComponent): Promise<IEstablishmentWriteResponse> {

        return await Network.putJson(ProductType.COMMUNITY, `/community/establishment/address/${communityId}`, data, component);
    }

    /**
     * Référentiels lus chez Google. Ils ne sont demandés qu'à l'ouverture du formulaire concerné : ils pèsent
     * sur un quota partagé par tout le parc.
     */
    public static async searchCategories(communityId: string,
                                         query: string,
                                         component?: INetworkComponent): Promise<ICommunityEstablishmentCategoryIncoming[]> {

        let request = await Network.get(ProductType.COMMUNITY,
            `/community/establishment/reference/categories/${communityId}?query=${encodeURIComponent(query)}`, component);

        // undefined pour un appel en échec, tableau vide pour une recherche sans résultat : le panneau ne dit
        // pas la même chose dans les deux cas, et ne réessaie que dans le premier
        return request?.status === HttpStatus.OK ? request.data : undefined;
    }

    public static async updateAttributes(communityId: string,
                                         data: ICommunityEstablishmentAttributesOutgoing,
                                         component?: INetworkComponent): Promise<IEstablishmentWriteResponse> {

        return await Network.putJson(ProductType.COMMUNITY, `/community/establishment/attributes/${communityId}`, data, component);
    }

    public static async updateServices(communityId: string,
                                       data: ICommunityEstablishmentServicesOutgoing,
                                       component?: INetworkComponent): Promise<IEstablishmentWriteResponse> {

        return await Network.putJson(ProductType.COMMUNITY, `/community/establishment/services/${communityId}`, data, component);
    }

    public static async getAttributesReference(communityId: string,
                                               component?: INetworkComponent): Promise<ICommunityEstablishmentAttributesReferenceIncoming> {

        let request = await Network.get(ProductType.COMMUNITY, `/community/establishment/reference/attributes/${communityId}`, component);
        return request?.data;
    }

    public static async getReference(communityId: string,
                                     component?: INetworkComponent): Promise<ICommunityEstablishmentReferenceIncoming> {

        let request = await Network.get(ProductType.COMMUNITY, `/community/establishment/reference/${communityId}`, component);
        return request?.data;
    }

    /**
     * Modifications faites par Google lui-même. Chaque appel touche l'API partenaire : la page ne les demande
     * qu'au clic sur le bandeau qui les annonce.
     */
    public static async getGoogleUpdated(communityId: string,
                                         component?: INetworkComponent): Promise<ICommunityEstablishmentGoogleUpdatedIncoming> {

        let request = await Network.get(ProductType.COMMUNITY, `/community/establishment/google-updated/${communityId}`, component);
        return request?.data;
    }

    /** Accepter écrit la valeur de Google, refuser réaffirme la nôtre : dans les deux cas c'est une écriture. */
    public static async resolveGoogleUpdated(communityId: string,
                                             accept: boolean,
                                             component?: INetworkComponent): Promise<IEstablishmentWriteResponse> {

        return await Network.post(ProductType.COMMUNITY,
            `/community/establishment/google-updated/${communityId}`, {accept: accept}, component);
    }

    /*
     * photos : elles vivent sur l'API historique de Google, et chaque action est déjà une écriture — il n'y a
     * pas de brouillon possible
     */

    public static async listMedia(communityId: string,
                                  component?: INetworkComponent): Promise<ICommunityEstablishmentMediaIncoming[]> {

        let request = await Network.get(ProductType.COMMUNITY, `/community/establishment/media/${communityId}`, component);
        return request?.data;
    }

    public static async createMedia(communityId: string,
                                    data: ICommunityEstablishmentMediaOutgoing,
                                    component?: INetworkComponent): Promise<IEstablishmentMediaResponse> {

        return await Network.postJson(ProductType.COMMUNITY, `/community/establishment/media/${communityId}`, data, component);
    }

    public static async deleteMedia(communityId: string,
                                    name: string,
                                    component?: INetworkComponent): Promise<boolean> {

        let request = await Network.delete(ProductType.COMMUNITY,
            `/community/establishment/media/${communityId}?name=${encodeURIComponent(name)}`, component);

        return request?.data === true;
    }
}
